import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Checkbox, DatePicker, Descriptions, Empty, Form, Input,
  InputNumber, Modal, Popconfirm, Select, Space, Spin, Tabs, Tag, Tooltip,
  Typography, message,
} from 'antd';
import {
  ArrowLeftOutlined, CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  copyFromStage, createStage, deleteStage, getCompetition, getStages, updateStage,
} from '../api/competitions';
import apiErrorMessage from '../utils/apiError';
import StageParticipantsTab from '../components/competitions/StageParticipantsTab';
import StageTeamsTab from '../components/competitions/StageTeamsTab';
import StageFinanceTab from '../components/competitions/StageFinanceTab';
import StageTravelersTab from '../components/competitions/StageTravelersTab';
import StageDocumentsTab from '../components/competitions/StageDocumentsTab';
import StageOperationsTab from '../components/competitions/StageOperationsTab';
import { CompetitionDocumentsTab, CompetitionNotesTab } from '../components/competitions/CompetitionDocsNotes';

const { Title, Text } = Typography;

export const STAGE_STATUS = {
  draft:        { label: 'Taslak',          color: 'default',    hint: 'Veliye hiç görünmüyor' },
  candidates:   { label: 'Aday Toplama',    color: 'processing', hint: 'Katılım onayı bekleniyor' },
  fee_approval: { label: 'Ücret Onayı',     color: 'warning',    hint: 'Veliler ücreti onaylıyor' },
  confirmed:    { label: 'Kesinleşti',      color: 'success',    hint: 'Katılımcılar netleşti' },
  completed:    { label: 'Tamamlandı',      color: 'success',    hint: '' },
  cancelled:    { label: 'İptal',           color: 'error',      hint: 'Veliye görünmüyor' },
};

const CompetitionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [competition, setCompetition] = useState(null);
  const [stages, setStages] = useState([]);
  const [activeStage, setActiveStage] = useState(null);
  // Etaplar / Belgeler / Notlar - son ikisi etaptan bagimsiz calisiyor.
  const [mainTab, setMainTab] = useState('stages');
  const [loading, setLoading] = useState(true);
  const [stageModal, setStageModal] = useState({ open: false, record: null });
  const [copyModal, setCopyModal] = useState(false);
  const [form] = Form.useForm();
  const [copyForm] = Form.useForm();
  // Hook JSX prop'unun icinde cagrilmamali - modal kapaliyken de render
  // agacinda kaldigi icin calisir ama kosullu gorunur ve lint uyarisi verir.
  const watchedStatus = Form.useWatch('status', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: comp }, { data: st }] = await Promise.all([
        getCompetition(id),
        getStages(id),
      ]);
      setCompetition(comp);
      const list = st.data || [];
      setStages(list);
      setActiveStage((prev) => (prev && list.some((s) => String(s.id) === String(prev)) ? prev : (list[0]?.id ? String(list[0].id) : null)));
    } catch (e) {
      message.error(apiErrorMessage(e, 'Yarışma yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openStageModal = (record = null) => {
    setStageModal({ open: true, record });
    form.setFieldsValue(record
      ? {
          ...record,
          start_date: record.start_date ? dayjs(record.start_date) : null,
          end_date: record.end_date ? dayjs(record.end_date) : null,
          registration_deadline: record.registration_deadline ? dayjs(record.registration_deadline) : null,
        }
      : { status: 'draft', is_abroad: false });
  };

  const submitStage = async () => {
    try {
      const v = await form.validateFields();
      const payload = {
        ...v,
        start_date: v.start_date ? v.start_date.format('YYYY-MM-DD') : null,
        end_date: v.end_date ? v.end_date.format('YYYY-MM-DD') : null,
        registration_deadline: v.registration_deadline ? v.registration_deadline.format('YYYY-MM-DD') : null,
      };
      if (stageModal.record) {
        await updateStage(stageModal.record.id, payload);
        message.success('Etap güncellendi.');
      } else {
        await createStage(id, payload);
        message.success('Etap oluşturuldu.');
      }
      setStageModal({ open: false, record: null });
      form.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  const submitCopy = async () => {
    try {
      const v = await copyForm.validateFields();
      const { data } = await copyFromStage(activeStage, v);
      // Hicbir sey kopyalanmadiysa yesil "basarili" gostermek yaniltici:
      // kullanici bir sey olduğunu sanip sekmelerde arar. Sunucu zaten
      // sebebini yaziyor (hedefte zaten var).
      const kopyalanan = (data.fee_items || 0) + (data.installment_plans || 0) + (data.plans_date_filled || 0)
        + (data.documents || 0) + (data.teams || 0) + (data.participants || 0);
      if (data.documents_skipped > 0) {
        // Dosyasi bulunamayan belge SESSIZ GECMEMELI: kullanici "4 belgem
        // vardi, 3'u geldi" durumunu baska hicbir yerde goremez.
        message.warning(data.message, 8);
      } else if (kopyalanan === 0 && !data.info) {
        message.info(data.message);
      } else {
        message.success(data.message);
      }
      setCopyModal(false);
      copyForm.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  if (loading && !competition) {
    return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  }

  const currentStage = stages.find((s) => String(s.id) === String(activeStage));

  const renderStages = () => (
    <Card>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openStageModal()}>
          Yeni Etap
        </Button>
        {currentStage && (
          <>
            <Button icon={<EditOutlined />} onClick={() => openStageModal(currentStage)}>
              Etabı Düzenle
            </Button>
            <Tooltip title="Başka bir etaptaki takım ve kesinleşmiş katılımcıları bu etaba taşır">
              <Button
                icon={<CopyOutlined />}
                disabled={stages.length < 2}
                onClick={() => {
                  setCopyModal(true);
                  // Varsayilanlar tersine cevrildi: etabin KURULUMU gelir,
                  // ogrenciler gelmez. Gerekcesi kopyalama penceresindeki
                  // aciklamada ve backend copyFrom yorumunda.
                  copyForm.setFieldsValue({
                    copy_info: true,
                    copy_fee_items: true,
                    copy_installment_plans: true,
                    copy_due_dates: false,
                    copy_documents: true,
                    copy_teams: false,
                    copy_participants: false,
                  });
                }}
              >
                Başka Etaptan Kopyala
              </Button>
            </Tooltip>
            <Popconfirm
              title="Etap silinsin mi?"
              onConfirm={async () => {
                try {
                  await deleteStage(currentStage.id);
                  message.success('Etap silindi.');
                  setActiveStage(null);
                  load();
                } catch (e) {
                  message.warning(apiErrorMessage(e));
                }
              }}
              okText="Sil"
              cancelText="Vazgeç"
            >
              <Button danger icon={<DeleteOutlined />}>Etabı Sil</Button>
            </Popconfirm>
          </>
        )}
      </Space>

      {stages.length === 0 ? (
        <Empty description="Bu yarışmaya henüz etap eklenmemiş. İzmir / İstanbul / Roma gibi etapları buradan ekleyin. Etap olmadan da Belgeler ve Notlar sekmelerini kullanabilirsiniz." />
      ) : (
        <Tabs
          type="card"
          activeKey={activeStage}
          onChange={setActiveStage}
          items={stages.map((s) => ({
            key: String(s.id),
            label: (
              <Space size={4}>
                {s.name}
                <Tag color={STAGE_STATUS[s.status]?.color} style={{ marginInlineEnd: 0 }}>
                  {STAGE_STATUS[s.status]?.label || s.status}
                </Tag>
              </Space>
            ),
            children: <StagePanel stage={s} onChanged={load} />,
          }))}
        />
      )}
    </Card>
  );

  return (
    <div>
      <Space style={{ marginBottom: 8 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/competitions')}>Yarışmalar</Button>
      </Space>

      <Title level={3} style={{ marginBottom: 0 }}>{competition?.name}</Title>
      <Text type="secondary">
        {competition?.organization?.name} · {competition?.year}
      </Text>

      {/* Etaplar / Belgeler / Notlar. Belge ve notlar yarışmanın kendisine
          ait olduğu için etap tanımlanmadan da kullanılabiliyor. */}
      <Tabs
        style={{ marginTop: 16 }}
        activeKey={mainTab}
        onChange={setMainTab}
        items={[
          { key: 'stages', label: 'Etaplar', children: renderStages() },
          {
            key: 'documents',
            label: 'Belgeler',
            children: <CompetitionDocumentsTab competitionId={id} />,
          },
          {
            key: 'notes',
            label: 'Notlar',
            children: <CompetitionNotesTab competitionId={id} stages={stages} />,
          },
        ]}
      />

      <Modal
        title={stageModal.record ? 'Etabı Düzenle' : 'Yeni Etap'}
        open={stageModal.open}
        onOk={submitStage}
        onCancel={() => { setStageModal({ open: false, record: null }); form.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        width={720}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Etap Adı" rules={[{ required: true, message: 'Ad girin.' }]}>
            <Input placeholder="İzmir Bölge / Roma Dünya Finali" />
          </Form.Item>
          <Space wrap>
            <Form.Item name="location" label="Yer"><Input placeholder="İzmir" /></Form.Item>
            <Form.Item name="country" label="Ülke"><Input placeholder="Türkiye / İtalya" /></Form.Item>
            <Form.Item name="is_abroad" label="Yurt dışı mı" valuePropName="checked">
              <Checkbox>Yurt dışı (pasaport bilgisi gerekir)</Checkbox>
            </Form.Item>
          </Space>
          <Space wrap>
            <Form.Item name="start_date" label="Başlangıç"><DatePicker format="DD.MM.YYYY" /></Form.Item>
            <Form.Item name="end_date" label="Bitiş"><DatePicker format="DD.MM.YYYY" /></Form.Item>
            <Form.Item name="registration_deadline" label="Son Kayıt"><DatePicker format="DD.MM.YYYY" /></Form.Item>
            <Form.Item name="sort_order" label="Sıra"><InputNumber min={0} max={999} /></Form.Item>
          </Space>
          <Form.Item name="status" label="Durum" extra={STAGE_STATUS[watchedStatus]?.hint}>
            <Select options={Object.entries(STAGE_STATUS).map(([k, v]) => ({ value: k, label: v.label }))} />
          </Form.Item>
          <Form.Item
            name="info_text"
            label="Veliye Gösterilecek Tanıtım Metni"
            extra="Veli bu metni okuyup katılım onayını verir. Ekli PDF varsa Belgeler sekmesinden yükleyin."
          >
            <ReactQuill theme="snow" style={{ background: '#fff' }} />
          </Form.Item>
          <Form.Item name="notes" label="İç Not (veliye görünmez)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Başka Etaptan Kopyala"
        open={copyModal}
        onOk={submitCopy}
        onCancel={() => { setCopyModal(false); copyForm.resetFields(); }}
        okText="Kopyala"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        {/*
          Kutular 21.10.2026'da yeniden düzenlendi. Önceden yalnızca takım
          ve katılımcı vardı ve ikisi de varsayılanda AÇIKTI; kurumun asıl
          ihtiyacı ise bunun tersi: "FLL'nin 10 etabı var, ödeme şekilleri
          aynı, dosyalar aynı, notlar aynı ama öğrenciler her seferinde
          değişik." Yani kopyalanan şey etabın KURULUMU olmalı.
        */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Etabın kurulumu kopyalanır, kimlik bilgileri kopyalanmaz"
          description="Ad, yer, ülke ve tarihler kopyalanmaz — her etap ayrı bir yerde ve ayrı tarihte. Hedefte zaten varsa üzerine yazılmaz, atlanır; tekrar çalıştırmak güvenlidir."
        />
        <Form form={copyForm} layout="vertical">
          <Form.Item
            name="source_stage_id"
            label="Kaynak Etap"
            rules={[{ required: true, message: 'Kaynak etabı seçin.' }]}
          >
            <Select
              placeholder="Hangi etaptan kopyalanacak"
              options={stages
                .filter((s) => String(s.id) !== String(activeStage))
                .map((s) => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>

          <Form.Item name="copy_info" valuePropName="checked" style={{ marginBottom: 4 }}>
            <Checkbox>Bilgi metni ve iç not</Checkbox>
          </Form.Item>
          <Form.Item name="copy_fee_items" valuePropName="checked" style={{ marginBottom: 4 }}>
            <Checkbox>Ücret kalemleri</Checkbox>
          </Form.Item>
          <Form.Item name="copy_installment_plans" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>Taksit planı</Checkbox>
          </Form.Item>
          <div style={{ fontSize: 12, color: '#888', marginLeft: 24, marginBottom: 4 }}>
            Tutarlar ve taksit sayısı gelir.
          </div>
          {/*
            VADE TARİHİ AYRI KUTU, varsayılan KAPALI.

            Kaynağın tarihleri yeni etabın takvimine uymazsa taksitler daha
            doğduğu anda "gecikmiş" görünür ve gecikmiş taksit listesini
            kirletir — o yüzden bilinçli bir tercih olarak işaretleniyor.

            Açıklama "hedefte zaten duran satırların boş vadesi de dolar"
            diyor, çünkü kutuyu tiklemenin mevcut vadesiz satırlara ne
            yapacağı sorulmazsa anlaşılmıyordu (canlıda bir kez "yine
            çalışmadı" denmesinin sebebi tam olarak buydu).
          */}
          <Form.Item name="copy_due_dates" valuePropName="checked" style={{ marginBottom: 0, marginLeft: 24 }}>
            <Checkbox>Vade tarihleriyle birlikte kopyala</Checkbox>
          </Form.Item>
          <div style={{ fontSize: 12, color: '#888', marginLeft: 48, marginBottom: 8 }}>
            Hedefte <b>zaten duran</b> taksitlerin boş vadeleri de doldurulur;
            elle girdiğiniz tarihlere dokunulmaz. Tarihler kaynak etabın
            takvimine ait — kopyaladıktan sonra kontrol edin.
          </div>
          <Form.Item name="copy_documents" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>Belgeler</Checkbox>
          </Form.Item>
          <div style={{ fontSize: 12, color: '#888', marginLeft: 24, marginBottom: 12 }}>
            Yalnızca etaba ait belgeler; öğrenciye ait olanlar (pasaport vb.) kopyalanmaz.
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
              Aşağıdakiler yeni etap kurarken genelde <b>gerekmez</b> — öğrenciler
              sonradan elle eklenir. Bir üst etaba kadro taşıyorsanız işaretleyin.
            </div>
            <Form.Item name="copy_teams" valuePropName="checked" style={{ marginBottom: 4 }}>
              <Checkbox>Takımları kopyala</Checkbox>
            </Form.Item>
            <Form.Item name="copy_participants" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Katılımcıları kopyala (ücreti onaylamış ve kesinleşmiş olanlar)</Checkbox>
            </Form.Item>
            <div style={{ fontSize: 12, color: '#888', marginLeft: 24 }}>
              Kopyalanırsa onaylar sıfırlanır; veliler yeni etap için tekrar onay verir.
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

// Bir etabın içindeki 7 alt sekme
const StagePanel = ({ stage, onChanged }) => {
  const [tab, setTab] = useState('overview');

  const items = [
    {
      key: 'overview',
      label: 'Genel',
      children: <StageOverview stage={stage} />,
    },
    { key: 'participants', label: 'Adaylar & Onaylar', children: <StageParticipantsTab stage={stage} onChanged={onChanged} /> },
    { key: 'teams',        label: 'Takımlar',          children: <StageTeamsTab stage={stage} /> },
    { key: 'finance',      label: 'Ücret & Taksitler', children: <StageFinanceTab stage={stage} /> },
    { key: 'documents',    label: 'Belgeler',          children: <StageDocumentsTab stage={stage} /> },
    { key: 'operations',   label: 'Çalışmalar & Görevler', children: <StageOperationsTab stage={stage} /> },
  ];

  // Pasaport sekmesi yalnizca yurt disi etaplarinda anlamli - yurt ici bir
  // etapta bos bir sekme gostermek kafa karistirir.
  if (stage.is_abroad) {
    items.splice(5, 0, { key: 'travelers', label: 'Pasaport Listesi', children: <StageTravelersTab stage={stage} /> });
  }

  return <Tabs activeKey={tab} onChange={setTab} items={items} />;
};

const StageOverview = ({ stage }) => {
  const fmt = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '—');

  return (
    <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
      <Descriptions.Item label="Etap">{stage.name}</Descriptions.Item>
      <Descriptions.Item label="Durum">
        <Tag color={STAGE_STATUS[stage.status]?.color}>{STAGE_STATUS[stage.status]?.label}</Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>{STAGE_STATUS[stage.status]?.hint}</Text>
      </Descriptions.Item>
      <Descriptions.Item label="Yer">
        {[stage.location, stage.country].filter(Boolean).join(' / ') || '—'}
        {stage.is_abroad && <Tag color="blue" style={{ marginLeft: 8 }}>Yurt dışı</Tag>}
      </Descriptions.Item>
      <Descriptions.Item label="Tarih">
        {fmt(stage.start_date)}{stage.end_date ? ` – ${fmt(stage.end_date)}` : ''}
      </Descriptions.Item>
      <Descriptions.Item label="Son Kayıt">{fmt(stage.registration_deadline)}</Descriptions.Item>
      <Descriptions.Item label="Katılımcı">{stage.participants_count ?? 0}</Descriptions.Item>
      <Descriptions.Item label="İç Not" span={2}>{stage.notes || '—'}</Descriptions.Item>
    </Descriptions>
  );
};

export default CompetitionDetail;
